import { CellDoubleClickedEvent, ICellRendererParams } from 'ag-grid-community';
import { useEffect, useMemo, useState } from 'react';

import { IManagedNote, NotesCompactView } from 'core/components';
import { useAgGridClientTableContext } from 'core/states';
import { INote } from 'core/types';
import { getAgGridTableCellData } from 'core/utils';
import { getMappedNotesArrByType, QuotePartNoteAddButton } from 'quotes/components';
import { QuotePartColId } from 'quotes/components/QuotePartList';
import { QuoteEntityType, QuotePartNoteType, QuoteType } from 'quotes/enums';
import { useQuotePartActions } from 'quotes/hooks';
import { ICustomerQuotePart, IQuotePartNote } from 'quotes/types';

interface IProps extends ICellRendererParams {
  isRowEditable: ({ data }: { data: ICustomerQuotePart }) => boolean;
  entityType?: QuoteEntityType;
  quoteType?: QuoteType;
}

const NotesCell = ({ isRowEditable = null, entityType, quoteType, ...props }: IProps) => {
  const [isOpenManageNotes, setIsOpenManageNotes] = useState(false);

  const { updateRows } = useAgGridClientTableContext();

  const { removeQuotePartNote, upsertQuotePartNote } = useQuotePartActions();

  const data = getAgGridTableCellData(props, QuotePartColId.NOTES);

  const quoteParts = useMemo(() => [data], [data]);

  // A line always mirrors its quote's alternate flag, so the row is a faithful source for it.
  const isAlternateVersionEnabled = data?.alternateVersionEnabled === 1;

  const mappedUndiscardableItemNotes = useMemo(
    () => getMappedNotesArrByType(data, QuotePartNoteType.UNDISCARDABLE_ITEM, entityType),
    [data, entityType]
  );
  const mappedDefaultNotes = useMemo(
    () => getMappedNotesArrByType(data, QuotePartNoteType.DEFAULT, entityType),
    [data, entityType]
  );
  const mappedLeadTimeNotes = useMemo(
    () => getMappedNotesArrByType(data, QuotePartNoteType.LEAD_TIME, entityType),
    [data, entityType]
  );
  // Gates the rendered cell (display and the add form) only: a counterparty surface renders no
  // internal notes even if a payload ever carried them. The notes column's valueGetter returns
  // raw `data?.notes`, so CSV/Excel export and the text filter read notes straight off the row
  // and never pass through here — BE-side stripping in Quote_VendorPartListManager is the sole
  // guarantee on that path.
  const mappedInternalNotes = useMemo(
    () =>
      QuoteType.CUSTOMER === quoteType
        ? getMappedNotesArrByType(data, QuotePartNoteType.INTERNAL, entityType)
        : [],
    [data, entityType, quoteType]
  );
  const mappedBuyerNotes = useMemo(
    () => getMappedNotesArrByType(data, QuotePartNoteType.BUYER, entityType),
    [data, entityType]
  );
  const mappedPickerNotes = useMemo(
    () => getMappedNotesArrByType(data, QuotePartNoteType.PICKER, entityType),
    [data, entityType]
  );
  const mappedSupshipNotes = useMemo(
    () => getMappedNotesArrByType(data, QuotePartNoteType.SUPSHIP, entityType),
    [data, entityType]
  );

  const mappedCombinedNotes = useMemo(
    () => [
      ...mappedUndiscardableItemNotes,
      ...mappedDefaultNotes,
      ...mappedLeadTimeNotes,
      ...mappedInternalNotes,
      ...mappedBuyerNotes,
      ...mappedPickerNotes,
      ...mappedSupshipNotes,
    ],
    [
      mappedUndiscardableItemNotes,
      mappedDefaultNotes,
      mappedLeadTimeNotes,
      mappedInternalNotes,
      mappedBuyerNotes,
      mappedPickerNotes,
      mappedSupshipNotes,
    ]
  );

  const onCellDoubleClicked = () => setIsOpenManageNotes(true);

  useEffect(() => {
    const handleDoubleClick = (eventParams: CellDoubleClickedEvent) => {
      if (
        eventParams.node.rowIndex === props.node.rowIndex &&
        eventParams.column.getColId() === props.column.getColId()
      ) {
        onCellDoubleClicked();
      }
    };

    props.api.addEventListener('cellDoubleClicked', handleDoubleClick);

    return () => {
      !props.api.isDestroyed() &&
        props.api.removeEventListener('cellDoubleClicked', handleDoubleClick);
    };
  }, [props.api, props.column, props.node.rowIndex]);

  if (!data) {
    return props.value;
  }

  const editable = isRowEditable && isRowEditable({ data });

  const onSubmitEditNote = async (data: Partial<INote>) => {
    const { success, quotePart } = await upsertQuotePartNote({
      quotePartNote: data as IQuotePartNote,
    });

    if (success) {
      updateRows({ update: [quotePart] });
    }

    return { success };
  };

  const onDeleteNote = async (note: Partial<INote>) => {
    const { success, quoteParts } = await removeQuotePartNote({
      ids: [note.id],
    });

    if (success) {
      updateRows({ update: quoteParts });
    }
  };

  return (
    <NotesCompactView
      compactPartNotes={[
        ...mappedUndiscardableItemNotes,
        ...getFirstNotesEachType([
          mappedDefaultNotes,
          mappedLeadTimeNotes,
          mappedInternalNotes,
          mappedBuyerNotes,
          mappedPickerNotes,
          mappedSupshipNotes,
        ]),
      ]}
      totalNotes={mappedCombinedNotes}
      hasManageNotes={editable}
      manageNotesIsOpenExternal={isOpenManageNotes}
      updateManageNotesIsOpenExternal={setIsOpenManageNotes}
      addButton={
        <QuotePartNoteAddButton
          quoteParts={quoteParts}
          level={1}
          entityType={entityType}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          quoteType={quoteType}
        />
      }
      listAddButton={
        <QuotePartNoteAddButton
          quoteParts={quoteParts}
          plainButton
          entityType={entityType}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          quoteType={quoteType}
        />
      }
      noteProps={{ onSubmitEditNote, onDeleteNote }}
    />
  );
};

export default NotesCell;

const getFirstNotesEachType = (notesLists: IManagedNote[][], notesNeeded = 2): IManagedNote[] => {
  const result: IManagedNote[] = [];

  for (const notesList of notesLists) {
    if (notesList.length > 0 && result.length < notesNeeded) {
      result.push(notesList[0]);
    }
  }

  for (const notesList of notesLists) {
    if (notesList.length > 0 && result.length < notesNeeded) {
      result.push(...notesList.slice(1, notesNeeded - result.length + 1));
    }
  }

  return result;
};
